import { useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CheckIcon,
  Dialog,
  Flex,
  Paragraph,
  Text,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import TwoFactor from '~publish/pages/Settings/Profile/components/TwoFactor'
import { useAcceptInvitation } from '~publish/pages/Settings/Team/hooks/useAcceptInvitation'

import invitationIllustration from './assets/invitation-illustration.svg'
import invitationIllustrationWelcome from './assets/invitation-illustration-welcome.svg'
import { markJustJoinedOrg } from './justJoinedOrg'
import type { InvitationModalData } from './useInvitationModal'

import styles from './InvitationModal.module.css'

/** What a brand-new user can do in Buffer, shown in the welcome variant. */
const WELCOME_HIGHLIGHTS = [
  'Schedule, publish and reply to content across all your social channels',
  'Plan and collaborate with your team in one place',
  "Track what's working with built-in analytics and more",
]

function errorMessageFor(kind: string, message: string): string {
  switch (kind) {
    case 'expired':
      return 'This invitation has expired. Ask your team admin to send a new one.'
    case 'emailMismatch':
      return "You're signed in with a different email than this invitation was sent to. Sign out and try again."
    case 'alreadyMember':
      return "You're already a member of this team."
    case 'network':
      return 'Could not reach the server. Check your connection and try again.'
    default:
      return message || 'Something went wrong. Please try again.'
  }
}

/**
 * The illustration sits inside a purple frame that matches the artwork's own
 * `#D1BDFF` backdrop (the `--color-illustration-card-media-background` token),
 * so the 8px inset reads as an even border rather than a seam. The frame's
 * radius matches the modal corner; the image radius is one step smaller so the
 * inner and outer curves stay concentric across the 8px padding.
 *
 * The welcome variant uses a taller cut of the same artwork with the two
 * speech bubbles spaced further apart (`invitation-illustration-welcome.svg`),
 * which reads better in its larger, side-by-side layout.
 */
const Illustration = ({ src }: { src: string }): JSX.Element => (
  <div className={styles.illustrationFrame}>
    <img src={src} alt="" aria-hidden="true" className={styles.illustration} />
  </div>
)

export interface InvitationModalProps {
  invitation: InvitationModalData
  open: boolean
  /** Dismiss without accepting — only the welcome variant is dismissable. */
  onClose: () => void
}

/**
 * In-app invitation modal shown on landing, replacing the former full-page
 * `/invite/accept` experience. Two variants share one component:
 *
 * - `accept` — existing users (logged in, or logged in after the login app).
 *   Vertical layout with an "Accept Invite" CTA. On success it flags the
 *   just-joined org so the org-switcher coach mark fires after reload.
 * - `welcome` — brand-new Buffer users. Wider intro layout with a checklist
 *   and an "Accept Invite" CTA (still runs accept; no coach mark afterwards).
 *
 * All variants run `acceptInvitation`, which hard-reloads so the legacy Redux
 * store re-bootstraps into the inviting org.
 *
 * When the inviting org enforces 2FA and the invitee hasn't set it up
 * (`requiresTwoFactorSetup`), the CTA routes through the forced-setup wizard
 * (`TwoFactor forced`, the same dialog the `/setup-2fa-required` gate uses)
 * and accepts on completion. Setup must come first: the server's accept gate
 * rejects a 2FA-less invitee with a typed `TwoFactorSetupRequiredError`,
 * which the accept handler answers by opening the same wizard. The two
 * dialogs swap rather than stack — the invitation modal hides while the
 * wizard is open and returns when it closes.
 */
export const InvitationModal = ({
  invitation,
  open,
  onClose,
}: InvitationModalProps): JSX.Element => {
  const {
    jwt,
    variant,
    organizationId,
    organizationName,
    inviterDisplay,
    requiresTwoFactorSetup,
  } = invitation
  const { account } = useAccount()
  const { acceptInvitation, loading: accepting } = useAcceptInvitation()
  const [error, setError] = useState<string | null>(null)
  const [isSettingUpTwoFactor, setIsSettingUpTwoFactor] = useState(false)
  // The server gate rejected an accept even though the invitation query
  // didn't flag the requirement (e.g. enforcement switched on after the
  // invitation resolved) — keeps the CTA on the setup path afterwards.
  const [gateRejectedAccept, setGateRejectedAccept] = useState(false)

  const isWelcome = variant === 'welcome'
  // `!account?.tfa` retires the setup step the moment the account shows 2FA:
  // the query flag goes stale mid-flow once the wizard confirms 2FA (it
  // refetches the account), including when the user bails on the final
  // recovery-code step after 2FA was already enabled.
  const needsTwoFactorSetup =
    (requiresTwoFactorSetup || gateRejectedAccept) && !account?.tfa

  const handleAccept = async (): Promise<void> => {
    setError(null)
    const outcome = await acceptInvitation({ jwt })

    if (outcome.kind === 'twoFactorRequired') {
      // The CTA normally routes through setup before accepting, so this only
      // fires when the pre-check missed; answer it the same way — open the
      // wizard — instead of surfacing an error banner.
      setGateRejectedAccept(true)
      setIsSettingUpTwoFactor(true)
      return
    }

    if (outcome.kind === 'success') {
      // `acceptInvitation` set `currentOrganization` server-side. Existing
      // users get the org-switcher coach mark after the reload; brand-new
      // users (welcome variant) don't, so only flag for the accept variant.
      if (!isWelcome) {
        markJustJoinedOrg(organizationId)
      }
      // Hard reload (not a soft navigate): the legacy Redux store keeps its
      // own org list and a client-side navigate dispatches the org switch
      // before that list refreshes, crashing downstream reducers. A full
      // refresh re-bootstraps the store with the right org from the session.
      window.location.assign('/')
      return
    }

    setError(errorMessageFor(outcome.kind, outcome.message))
  }

  // Only the welcome variant can be dismissed; the accept variant has no
  // close affordance so the only way forward is to accept.
  const handleOpenChange = (next: boolean): void => {
    if (!next && isWelcome) {
      onClose()
    }
  }

  const errorMessage = error && (
    <Paragraph className={styles.error}>{error}</Paragraph>
  )

  const startTwoFactorSetup = (): void => {
    setError(null)
    setIsSettingUpTwoFactor(true)
  }

  const twoFactorNotice = needsTwoFactorSetup && (
    <Paragraph className={styles.twoFactorNotice}>
      {organizationName} requires two-factor authentication for all team
      members. Set it up to accept your invite.
    </Paragraph>
  )

  // Stays mounted (not just open) for the whole wizard run: confirming 2FA
  // refetches the account, which drops `needsTwoFactorSetup` while the user
  // is still on the recovery-code step.
  const twoFactorWizard = (needsTwoFactorSetup || isSettingUpTwoFactor) && (
    <TwoFactor
      forced
      open={isSettingUpTwoFactor}
      onOpenChange={setIsSettingUpTwoFactor}
      onSetupComplete={(): void => {
        void handleAccept()
      }}
    />
  )

  const primaryCtaLabel = needsTwoFactorSetup
    ? 'Set Up Two-Factor Authentication'
    : 'Accept Invite'
  const handlePrimaryCta = needsTwoFactorSetup
    ? startTwoFactorSetup
    : handleAccept

  if (isWelcome) {
    return (
      <>
        <Dialog
          open={open && !isSettingUpTwoFactor}
          onOpenChange={handleOpenChange}
        >
          <Dialog.Content size="xlarge" className={styles.welcomeContent}>
            <Flex className={styles.welcomeLayout}>
              <Flex
                direction="column"
                gap="md"
                className={styles.welcomeText}
                align="start"
              >
                {/* size="large" keeps the Stolzl heading font at 20px/125%
                  (Dialog.Title defaults to size="md", which is the body
                  font) — matches the design's heading/lg spec. */}
                <Dialog.Title size="large">
                  You&apos;ve been invited to {organizationName} 🎉
                </Dialog.Title>
                <Paragraph>Here&apos;s what you can do in Buffer:</Paragraph>
                <ul className={styles.highlights}>
                  {WELCOME_HIGHLIGHTS.map((highlight) => (
                    <li key={highlight}>
                      <Text icon={<CheckIcon className={styles.checkIcon} />}>
                        {highlight}
                      </Text>
                    </li>
                  ))}
                </ul>
                {twoFactorNotice}
                {errorMessage}
                <Button
                  variant="primary"
                  size="large"
                  onClick={handlePrimaryCta}
                  loading={accepting}
                  className={styles.welcomeCta}
                >
                  {primaryCtaLabel}
                </Button>
              </Flex>
              <div className={styles.welcomeIllustration}>
                <Illustration src={invitationIllustrationWelcome} />
              </div>
            </Flex>
            <Dialog.CloseButton />
          </Dialog.Content>
        </Dialog>
        {twoFactorWizard}
      </>
    )
  }

  return (
    <>
      <Dialog
        open={open && !isSettingUpTwoFactor}
        onOpenChange={handleOpenChange}
      >
        <Dialog.Content size="medium" className={styles.acceptContent}>
          <Illustration src={invitationIllustration} />
          <Flex
            direction="column"
            gap="sm"
            align="center"
            className={styles.acceptBody}
          >
            <Dialog.Title size="large" className={styles.acceptTitle}>
              You&apos;ve been invited to join {organizationName} 🎉
            </Dialog.Title>
            <Dialog.Description className={styles.acceptDescription}>
              {inviterDisplay} invited you to their Buffer team.
            </Dialog.Description>
            {twoFactorNotice}
            {errorMessage}
            <Button
              variant="primary"
              size="large"
              onClick={handlePrimaryCta}
              loading={accepting}
            >
              {primaryCtaLabel}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog>
      {twoFactorWizard}
    </>
  )
}
